import { LoadingSpinner } from "../../core/components/LoadingSpinner";
import { useEffect, useMemo, useRef } from 'react';
import { useSortBy, useTable } from 'react-table';
import { Box, Text, useTombac } from 'tombac';
import { ChevronDownIcon, ChevronUpIcon } from 'tombac-icons';
import { useUsersListQuery } from "./usersApiHooks";
import formatDate from 'date-fns/format';
import { isMeDetailsFallback, useLoginAsMutiation, useMeDetailsQuery, useMeQuery } from 'legoland-sdk/dist/experimental';
import { Pagination } from "../../contracts/list/Pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "../../contracts/list/Table";
import { createUsersListColumns } from "./createUsersListColumns";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function isSortByEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}
const defaultSortBy = [];
export const UsersList = ({
  filters,
  onPageChange,
  onSortByChange,
  sortBy = defaultSortBy,
  page,
  variant = 'default'
}) => {
  const tombac = useTombac();
  const {
    data,
    isLoading,
    isFetching
  } = useUsersListQuery({
    filters,
    sortBy,
    page
  }, {
    keepPreviousData: true
  });
  const {
    mutateAsync: loginAs
  } = useLoginAsMutiation();
  const tableData = useMemo(() => data?.content ?? [], [data]);
  const meQuery = useMeQuery();
  const role = meQuery.data?.me?.role;
  const meDetailsQuery = useMeDetailsQuery();
  const meDetails = meDetailsQuery?.data?.meDetails;
  const formatDateBySettings = date => {
    const dateFormat = !meDetails || isMeDetailsFallback(meDetails) || meDetails.settings.dateFormat === 'DDMMYYYY' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
    const clockFormat = !meDetails || isMeDetailsFallback(meDetails) || meDetails.settings.clockFormat === '24' ? 'HH:mm' : 'p';
    return formatDate(new Date(date), `${dateFormat} ${clockFormat}`);
  };
  const loginAsUser = email => {
    loginAs({
      username: email
    }).then(() => {
      window.location.assign('/dashboard');
    });
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
    state: {
      sortBy: tableSortBy
    }
  } = useTable({
    columns: useMemo(() => createUsersListColumns(variant, role, formatDateBySettings, loginAsUser), []),
    data: tableData,
    disableMultiSort: true,
    disableSortBy: !onSortByChange,
    initialState: {
      sortBy
    },
    manualSortBy: true
  }, useSortBy);
  const tableSortByRef = useRef(tableSortBy);
  useEffect(function handleSortByPropChange() {
    if (!isSortByEqual(sortBy, tableSortByRef.current)) {
      // condition prevents infinite loop
      setSortBy(sortBy);
    }
  }, [sortBy]);
  useEffect(function handleTableSortByChange() {
    if (!onSortByChange) return;
    onSortByChange(tableSortBy);
    tableSortByRef.current = tableSortBy;
  }, [tableSortBy]);
  return isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {}) : /*#__PURE__*/_jsxs(Box, {
    $boxSizing: "content-box",
    $margin: "3sp auto",
    $maxWidth: "1440u",
    $padding: variant === 'narrow' ? '0' : '0 3sp',
    children: [variant !== 'narrow' && /*#__PURE__*/_jsx(Pagination, {
      $paddingBottom: "2sp",
      page: page,
      itemCount: data?.totalElements,
      onPageChange: page => onPageChange && onPageChange(page),
      isListFetching: isFetching
    }), tableData.length === 0 ? /*#__PURE__*/_jsx(Box, {
      $display: "flex",
      $justifyContent: "center",
      $mt: "3sp",
      children: /*#__PURE__*/_jsx(Text, {
        variant: "secondary",
        children: "No user matches the criteria"
      })
    }) : /*#__PURE__*/_jsxs(Table, {
      ...getTableProps(),
      children: [/*#__PURE__*/_jsx(Thead, {
        children: headerGroups.map(headerGroup => {
          const {
            key: headerGroupKey,
            ...headerGroupProps
          } = headerGroup.getHeaderGroupProps();
          return /*#__PURE__*/_jsx(Tr, {
            ...headerGroupProps,
            children: headerGroup.headers.map(column => {
              const {
                key: columnHeaderKey,
                ...columnHeaderProps
              } = column.getHeaderProps(column.getSortByToggleProps({
                style: {
                  width: tombac.unit(Number(column.width))
                }
              }));
              return /*#__PURE__*/_jsxs(Th, {
                ...columnHeaderProps,
                children: [column.render('Header'), ' ', column.isSorted && (column.isSortedDesc ? /*#__PURE__*/_jsx(ChevronDownIcon, {
                  size: "xs"
                }) : /*#__PURE__*/_jsx(ChevronUpIcon, {
                  size: "xs"
                }))]
              }, columnHeaderKey);
            })
          }, headerGroupKey);
        })
      }), /*#__PURE__*/_jsx(Tbody, {
        ...getTableBodyProps(),
        children: rows.map(row => {
          prepareRow(row);
          const {
            key: rowKey,
            ...rowProps
          } = row.getRowProps();
          return /*#__PURE__*/_jsx(Tr, {
            ...rowProps,
            children: row.cells.map(cell => {
              const {
                key: cellKey,
                ...cellProps
              } = cell.getCellProps();
              return /*#__PURE__*/_jsx(Td, {
                $overflow: "hidden",
                $whiteSpace: "nowrap",
                $textOverflow: cell.column.id === 'loginAs' ? 'clip' : 'ellipsis',
                ...cellProps,
                children: cell.render('Cell')
              }, cellKey);
            })
          }, rowKey);
        })
      })]
    })]
  });
};